import { computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useMainStore } from '@/store/main'
import { useCookieConsentStore } from '@/store/cookieConsent'
import { useSurveyStore } from '@/store/survey'

import analytics from '@/modules/analytics'

export function useCookiebot() {
	const mainStore = useMainStore()
	const consentStore = useCookieConsentStore()
	const surveyStore = useSurveyStore()

	const { getCountryCode: countryCode, getIsEUCountry: isEU } = storeToRefs(mainStore)
	const { getActiveGroups: activeGroups } = storeToRefs(consentStore)

	const isOptInMode = computed(() => isEU.value || countryCode.value === 'uk')
	const bannerType = computed(() => (isOptInMode.value ? 'extended' : 'general'))

	function syncStateFromCookiebot() {
		const cookiebot = window.Cookiebot
		if (!cookiebot?.consent) return

		consentStore.setInited(true)
		consentStore.setResponded(Boolean(cookiebot.hasResponse))
		consentStore.setCategories({
			necessary: cookiebot.consent.necessary,
			preferences: cookiebot.consent.preferences,
			statistics: cookiebot.consent.statistics,
			marketing: cookiebot.consent.marketing,
		})

		// Временный хак по требованию от Хлопонина
		// Юзер всегда соглашается на куки, даже если отказался от них
		enforceConsentPolicy(cookiebot)
		syncMarketingPurposesConsent(cookiebot.consent.marketing)
	}

	function enforceConsentPolicy(cookiebot) {
		// КРИТИЧНО: действуем, только пока есть что принимать.
		// submitCustomConsent перестреливает CookiebotOnConsentReady → syncStateFromCookiebot,
		// без этого guard'а получим бесконечную рекурсию.

		const hasNotOptionalConsent =
			!cookiebot.consent.preferences || !cookiebot.consent.statistics || !cookiebot.consent.marketing

		if (!hasNotOptionalConsent) return

		window.Cookiebot?.submitCustomConsent?.(true, true, true)
	}

	function syncMarketingPurposesConsent(hasMarketingConsent) {
		if (hasMarketingConsent) {
			surveyStore.addUserConsent('marketing')
		} else {
			surveyStore.removeUserConsent('marketing')
		}
	}

	function sendCookieConsentDoneEvent(userConsent) {
		setTimeout(() => {
			// cookie_type is `repeated string` in proto — encoder requires an iterable
			// (not undefined) even when empty.  activeGroups returns an array, but guard
			// defensively in case the store hasn't initialised yet.
			analytics.track('CookieConsentDone', {
				banner_type: bannerType.value,
				user_consent: Boolean(userConsent),
				cookie_type: Array.isArray(activeGroups.value) ? activeGroups.value : [],
			})
		}, 500)
	}

	function handleDialogDisplay() {
		consentStore.setInited(true)
	}

	function handleAccept() {
		const wasResponded = consentStore.hasResponded
		syncStateFromCookiebot()

		if (!wasResponded) {
			sendCookieConsentDoneEvent(true)
		}
	}

	function handleDecline() {
		const wasResponded = consentStore.hasResponded
		syncStateFromCookiebot()

		if (!wasResponded) {
			sendCookieConsentDoneEvent(false)
		}
	}

	onMounted(() => {
		window.addEventListener('CookiebotOnDialogDisplay', handleDialogDisplay)
		window.addEventListener('CookiebotOnAccept', handleAccept)
		window.addEventListener('CookiebotOnDecline', handleDecline)

		if (window.Cookiebot?.hasResponse) {
			syncStateFromCookiebot()
		}
	})
}
